<template>
    <span :class="{ 'truncate': truncate }">
        <template v-if="column === 'number'">
            <button
                class="flex items-center"
                :class="seen ? 'cursor-default' : 'group-hover:text-blue-500'"
                @click="triggerUpdatePayment"
            >
                <nio-optional :value="payment.invoice_number" />
                <nio-icon
                    class="invisible text-blue-500 ml-2 | group-hover:visible"
                    name="bold/arrow-button-circle-right-1"
                    v-if="!seen"
                />
            </button>
        </template>

        <template v-if="column === 'contact'">
            {{ fullName }}
        </template>

        <template v-if="column === 'amount'">
            {{ $n(amountInDollars, 'currency') }}
        </template>

        <template v-if="column === 'status'">
            <nio-badge
                :color="statusColor"
                :tag="badgeIsClickable ? 'button' : 'span'"
                @click="onStatusClick"
                v-tooltip="statusTooltip"
            >
                {{ statusText }}
            </nio-badge>
        </template>

        <template v-if="column === 'actions'">
            <nio-dropdown>
                <template #trigger>
                    <button class="link-gray">
                        <nio-icon name="bold/navigation-menu-horizontal" />
                    </button>
                </template>

                <nio-dropdown-item
                    :disabled="seen"
                    icon="regular/pencil-write"
                    @click="triggerUpdatePayment"
                >
                    {{ $t('contacts.contactsList.edit') }}
                </nio-dropdown-item>
                <nio-dropdown-item
                    :disabled="sent"
                    icon="regular/accounting-invoice-mail"
                    @click="triggerSendPaymentLink"
                >
                    {{ $t('payments.paymentsList.sendPaymentLink') }}
                </nio-dropdown-item>
                <hr>
                <nio-dropdown-item
                    class="transition duration-300 hover:text-red-500"
                    :disabled="paid || (seen && !expired)"
                    icon="regular/bin-2-alternate"
                    @click="triggerDeletePayment"
                >
                    {{ $t('contacts.contactsList.delete') }}
                </nio-dropdown-item>
            </nio-dropdown>
        </template>
    </span>
</template>

<script>
    import { mapState } from 'vuex';

    export default {
        props: {
            column: {
                type: String,
                required: true,
            },
            payment: {
                type: Object,
                required: true,
            },
            truncate: {
                type: Boolean,
                default: true,
            },
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            fullName() {
                return this.formatFullName(this.payment.contact);
            },

            sent() {
                return !!this.payment.sent_at;
            },

            seen() {
                return !!this.payment.seen_at;
            },

            paid() {
                return !!this.payment.paid_at;
            },

            expired() {
                if (!this.payment.expires_at) {
                    return false;
                }

                return moment(this.payment.expires_at).isBefore(moment());
            },

            amountInDollars() {
                return this.payment.amount / 100;
            },

            status() {
                if (this.payment.paid_at) {
                    return 'paid';
                }

                if (this.expired) {
                    return 'expired';
                }

                if (this.payment.seen_at) {
                    return 'seen';
                }

                if (this.payment.sent_at) {
                    return 'sent';
                }

                return 'notSent';
            },

            statusColor() {
                switch (this.status) {
                    case 'sent':
                        return 'blue';
                    case 'seen':
                        return 'yellow';
                    case 'paid':
                        return 'green';
                    case 'expired':
                        return 'gray';
                    case 'notSent':
                    default:
                        return 'red';
                }
            },

            statusTooltip() {
                switch (this.status) {
                    case 'sent':
                        return this.formatDateTime(this.payment.sent_at);
                    case 'seen':
                        return this.formatDateTime(this.payment.seen_at);
                    case 'paid':
                        return this.formatDateTime(this.payment.paid_at);
                    case 'expired':
                    case 'notSent':
                    default:
                        return null;
                }
            },

            statusText() {
                switch (this.status) {
                    case 'sent':
                        return this.$t('payments.paymentsList.statuses.sent');
                    case 'seen':
                        return this.$t('payments.paymentsList.statuses.seen');
                    case 'paid':
                        return this.$t('payments.paymentsList.statuses.paid');
                    case 'expired':
                        return this.$t('payments.paymentsList.statuses.expired');
                    case 'notSent':
                    default:
                        return this.$t('payments.paymentsList.statuses.notSent');
                }
            },

            badgeIsClickable() {
                return this.status === 'notSent';
            },
        },

        methods: {
            triggerUpdatePayment() {
                if (this.seen) {
                    return;
                }

                this.$emit('update');
            },

            triggerSendPaymentLink() {
                this.$modal.open('sendPaymentLink', { payment: this.payment });
            },

            triggerDeletePayment() {
                if (this.paid) {
                    return;
                }

                this.$emit('delete');
            },

            onStatusClick() {
                if (this.badgeIsClickable) {
                    this.triggerSendPaymentLink();
                }
            },
        },
    };
</script>
